import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';

import ChartLineFill from '@/assets/icons/chart_line_fill.svg?component';
import FirstAidKitFill from '@/assets/icons/first_aid_kit_fill.svg?component';
import Louse from '@/assets/icons/louse.svg?component';
import { Spinner } from '@/components/atoms/Spinner';
import PageLayout from '@/components/Layout/PageLayout';
import ContentBlock from '@/components/molecules/ContentBlock';
import MetricCard from '@/components/molecules/MetricCard/MetricCard';
import {
    DEFAULT_SORT_BY,
    type SortBy,
    SortByDropdown,
} from '@/components/molecules/SortByDropdown';
import {
    LocationOverviewTable,
    useLocationOverviewRows,
} from '@/components/organisms/LocationOverviewTable';
import type { RetryProps } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { hasWoundScoringPen } from '@/domain/cages/speciesTraits';
import type {
    ClosedFishGroupRow,
    LocationAverages,
    LocationType,
    OverviewCage,
} from '@/domain/client';
import type { LocationsLicenseOverview } from '@/domain/licenses';
import type { DailyCageWeight, MarkHistoricalInput } from '@/domain/locations';
import type { ApiError } from '@/domain/shared/errors';
import {
    formatGrowthUnit,
    formatGrowthValue,
    formatLiceValue,
    formatWoundsValue,
} from '@/utils/localeFormatters';
import { isLandbasedLocation, isSmoltLocation } from '@/utils/locationTypeHelpers';

import { DashboardViewSwitcher } from '../components/DashboardViewSwitcher/DashboardViewSwitcher';

import WeightMeasurementList from './components/WeightMeasurementList/WeightMeasurementList';

import styles from './LocationDashboard.module.scss';

interface LocationDashboardPageContentProps {
    activeCages: OverviewCage[];
    inactiveCages: OverviewCage[];
    emptyCages: OverviewCage[];
    historicalCages: OverviewCage[];
    /**
     * Cohorts that finished in one of this location's cages. Rendered in the same
     * table behind the show-historical toggle. `undefined` when fish group tracking
     * is off.
     */
    closedFishGroups?: ClosedFishGroupRow[];
    locationId: number;
    locationName: string;
    locationType: LocationType;
    averages: LocationAverages;
    /** Yesterday's weight per pen; its own request, so it fails on its own. */
    weightStatus: RetryProps & { rows: DailyCageWeight[]; error: unknown };
    locationLicense: LocationsLicenseOverview | undefined;
    clientId: number;
    containerError: ApiError | null;
    containerRetry: RetryProps;
    containerLoading: boolean;
    hasAccessToMarkHistorical: boolean;
    isOptoScaleAdmin?: boolean;
    markHistorical: (changes: MarkHistoricalInput[]) => Promise<unknown>;
}

export default function LocationDashboardPageContent(props: LocationDashboardPageContentProps) {
    const { i18n } = useTranslation();
    const {
        clientId,
        activeCages,
        inactiveCages,
        emptyCages,
        historicalCages,
        closedFishGroups,
        locationId,
        locationName,
        locationType,
        averages,
        locationLicense,
        weightStatus,
        markHistorical,
        hasAccessToMarkHistorical,
        isOptoScaleAdmin = false,
        containerLoading,
        containerError,
        containerRetry,
    } = props;
    const hasAccessToFishHealth =
        isOptoScaleAdmin || (locationLicense?.licenses.fishHealthLicense.hasValidLicense ?? false);
    const hasAccessToLice =
        isOptoScaleAdmin || (locationLicense?.licenses.liceLicense.hasValidLicense ?? false);

    const [sortBy, setSortBy] = useState<SortBy>(DEFAULT_SORT_BY);
    const rows = useLocationOverviewRows(
        { activeCages, inactiveCages, emptyCages, historicalCages, closedFishGroups },
        sortBy
    );

    // A location can hold several species (WB-2207), so the wound box follows the pens rather
    // than the site default: one pen with wound scoring keeps it. A trait rather than a species
    // check, so a species added later is not silently excluded.
    const woundScoringPen = hasWoundScoringPen([...activeCages, ...inactiveCages, ...emptyCages]);

    if (containerLoading) {
        return (
            <PageLayout title={locationName} subtitle={t('location.dashboard.dashboard')}>
                <Spinner size="lg" fullScreen />
            </PageLayout>
        );
    }
    if (containerError) {
        return (
            <PageLayout title={locationName} subtitle={t('location.dashboard.dashboard')}>
                <SomethingHappened {...containerRetry} />
            </PageLayout>
        );
    }

    return (
        <PageLayout
            title={locationName}
            subtitle={t('location.dashboard.dashboard')}
            actions={<DashboardViewSwitcher />}>
            {!isSmoltLocation(locationType) && (
                <div className={styles.infoBoxContainer}>
                    <MetricCard
                        value={formatGrowthValue(averages.growth, i18n.language)}
                        suffix={formatGrowthUnit(t('daypartTag.day'))}
                        title={t('common.labels.growth')}
                        icon={ChartLineFill}
                        tone="info"
                        framed="surface"
                    />

                    {hasAccessToLice ? (
                        <MetricCard
                            value={formatLiceValue(averages.femaleLice, i18n.language)}
                            title={t('common.labels.lice')}
                            icon={Louse}
                            tone="warning"
                            framed="surface"
                        />
                    ) : (
                        ''
                    )}
                    {hasAccessToFishHealth && woundScoringPen ? (
                        <MetricCard
                            value={formatWoundsValue(averages.wounds, i18n.language)}
                            suffix="%"
                            title={t('common.labels.wound')}
                            icon={FirstAidKitFill}
                            tone="success"
                            framed="surface"
                        />
                    ) : (
                        ''
                    )}
                </div>
            )}

            <div className={styles.gridLayout}>
                <div id={locationName.replace(/\s+/g, '')}>
                    <ContentBlock
                        title={
                            isSmoltLocation(locationType) || isLandbasedLocation(locationType)
                                ? (t('common.labels.tank') ?? 'Tank')
                                : (t('common.labels.cage') ?? 'Cage')
                        }
                        headerActions={<SortByDropdown value={sortBy} onChange={setSortBy} />}>
                        <LocationOverviewTable
                            rows={rows}
                            locationId={locationId}
                            locationType={locationType}
                            locationLicense={locationLicense}
                            hasAccessToMarkHistorical={hasAccessToMarkHistorical}
                            isOptoScaleAdmin={isOptoScaleAdmin}
                            markHistorical={markHistorical}
                            hideMarkHistorical={false}
                            clientId={clientId}
                        />
                    </ContentBlock>
                </div>
                <div className={styles.verticalLayoutContainer}>
                    <ContentBlock
                        title={t('location.dashboard.weightMeasurements') ?? 'Weight measurements'}
                        titleLevel="h2"
                        titleSize="card"
                        subtitle={t('location.dashboard.totalMeasurementsYesterday')}>
                        {weightStatus.error ? (
                            <SomethingHappened
                                onRetry={weightStatus.onRetry}
                                isRetrying={weightStatus.isRetrying}
                            />
                        ) : (
                            <WeightMeasurementList rows={weightStatus.rows} />
                        )}
                    </ContentBlock>
                </div>
            </div>
        </PageLayout>
    );
}
